import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { ENDPOINT } from '~/constants';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { mapUpdateInputDataMutationData } from './mapUpdateInputDataMutationData';
import { queryKeysAssetReparsing } from './queryKeys';

/**
 * Updates the input data of a reparsing asset with the specific ID.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/change_reparsing_input_data_vadmin_asset_reparsing__reparsing_id__update_original_dln_data_post
 */
const updateReparsingInputData = async (
  reparsingId: string,
  body: ReturnType<typeof mapUpdateInputDataMutationData>,
) => {
  try {
    return await vestigasApi.post(
      ENDPOINT.ASSET_REPARSING.UPDATE_ORIGINAL_DLN_DATA(reparsingId),
      { json: body },
    );
  } catch (error) {
    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.updateReparsingInputData.error.400',
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.updateReparsingInputData.error.422',
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.updateReparsingInputData.error.generic',
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to update the input data of a reparsing asset.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/change_reparsing_input_data_vadmin_asset_reparsing__reparsing_id__update_original_dln_data_post
 */
export const useMutationUpdateReparsingInputData = (
  reparsingId: string,
  options?: UseMutationOptions,
): UseMutationResult<any, unknown, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn({ formData, originalAssetBody }) {
      const body = mapUpdateInputDataMutationData(formData, originalAssetBody);

      return updateReparsingInputData(reparsingId, body);
    },
    mutationKey: queryKeysAssetReparsing.updateReparsingInputData(reparsingId),
    onError(error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysAssetReparsing.getAll(),
      });
    },
    onSuccess() {
      ToastService.info(
        i18n.t(
          ($) => $.assetReparsing.mutation.updateReparsingInputData.success,
        ),
      );
    },
    ...options,
  });
};
